// src/components/CustomerVehicles.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  DirectionsCar,
  Add,
  Build,
} from "@mui/icons-material";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";

function CustomerVehicles() {
  const { customer } = useCustomerAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch vehicles from jobs (unique vehicles used)
  useEffect(() => {
    if (!customer) return;

    const jobsQuery = query(
      collection(db, "jobs"),
      where("customerName", "==", customer.name)
    );

    const unsubscribe = onSnapshot(jobsQuery, (snapshot) => {
      const vehiclesMap = new Map();
      
      snapshot.docs.forEach(doc => {
        const job = doc.data();
        const vehicleReg = job.vehicleRegistration;
        
        if (vehicleReg) {
          if (!vehiclesMap.has(vehicleReg)) {
            vehiclesMap.set(vehicleReg, {
              registration: vehicleReg,
              make: job.vehicleMake || "Unknown",
              model: job.vehicleModel || "",
              jobs: [],
              lastService: null,
            });
          }
          
          vehiclesMap.get(vehicleReg).jobs.push({
            id: doc.id,
            ...job,
          });
          
          // Update last service date
          if (job.status === "Completed" && job.completedDate) {
            const currentLast = vehiclesMap.get(vehicleReg).lastService;
            if (!currentLast || job.completedDate > currentLast) {
              vehiclesMap.get(vehicleReg).lastService = job.completedDate;
            }
          }
        }
      });

      const vehiclesList = Array.from(vehiclesMap.values());
      setVehicles(vehiclesList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [customer]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "Never";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h4" sx={{ color: "white", fontWeight: 700, mb: 1 }}>
            My Vehicles
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your fleet and service history
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #d97706 0%, #dc2626 100%)",
            },
          }}
        >
          Add Vehicle
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", color: "white" }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>{vehicles.length}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Vehicles</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "white" }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>
                {vehicles.reduce((sum, v) => sum + v.jobs.filter(j => j.status === "Completed").length, 0)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Services</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", color: "white" }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>
                {vehicles.reduce((sum, v) => sum + v.jobs.filter(j => 
                  ["Pending", "Travelling", "On Site", "In Progress"].includes(j.status)
                ).length, 0)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Active Jobs</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)", color: "white" }}>
            <CardContent>
              <Typography variant="h4" sx={{ color: "white", fontWeight: 700 }}>
                {vehicles.filter(v => v.lastService).length}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Serviced</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Vehicles List */}
      {vehicles.length === 0 ? (
        <Alert severity="info">
          No vehicles found. Vehicles will appear here once you've requested services.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {vehicles.map((vehicle, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                sx={{
                  p: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    boxShadow: "0 4px 12px rgba(245,158,11,0.2)",
                  },
                }}
              >
                {/* Vehicle Header */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <DirectionsCar sx={{ fontSize: 32, color: "white" }} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {vehicle.registration}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {vehicle.make} {vehicle.model}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${vehicle.jobs.length} jobs`}
                    color="primary"
                    size="small"
                  />
                </Box>

                {/* Vehicle Stats */}
                <Stack spacing={2} sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      Last Service:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatDate(vehicle.lastService)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      Completed Jobs:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {vehicle.jobs.filter(j => j.status === "Completed").length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      Active Jobs:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {vehicle.jobs.filter(j => 
                        ["Pending", "Travelling", "On Site", "In Progress"].includes(j.status)
                      ).length}
                    </Typography>
                  </Box>
                </Stack>

                {/* Actions */}
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Build />}
                    fullWidth
                  >
                    Service History
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    fullWidth
                    sx={{
                      background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #d97706 0%, #dc2626 100%)",
                      },
                    }}
                  >
                    Request Service
                  </Button>
                </Stack>

                {/* Recent Jobs */}
                {vehicle.jobs.length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                      Recent Jobs:
                    </Typography>
                    <Stack spacing={1}>
                      {vehicle.jobs.slice(0, 3).map((job, idx) => (
                        <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="caption">
                            {job.jobTemplateName || "Service"}
                          </Typography>
                          <Chip
                            label={job.status}
                            size="small"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                            color={
                              job.status === "Completed" ? "success" :
                              ["Pending", "Travelling"].includes(job.status) ? "warning" : "info"
                            }
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Info Card */}
      <Card sx={{ mt: 3, bgcolor: "rgba(59,130,246,0.05)" }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            📋 Fleet Management Tips
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Keep track of all your vehicles in one place<br />
            • View complete service history per vehicle<br />
            • Schedule regular maintenance to keep your fleet running smoothly
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default CustomerVehicles;
